/**
 * CouponDrawer — criar / editar cupom de desconto.
 *
 * V2 (com V2_035): code + target_type + target_id + discount_type
 *   + (discount_percent OU discount_cents conforme discount_type)
 *   + valid_from, valid_until, max_uses, is_active
 *
 * Internamente o form mantém um único campo `discount_value` (string) pra UX
 * simples. Na hidratação e no submit, mapeia pro campo correto do backend.
 */

import React, { useEffect, useState } from 'react';
import { Icon } from '@iconify/react';
import { couponsV2API } from '../../../services/api/v2/coupons';
import { centsToDisplay, parseInputToCents } from '../../../utils/currencyMask';
import { parseApiError, scrollToFirstError } from '../../../utils/apiErrors';
import Dropdown from '../../ui/Dropdown';
import Drawer from './Drawer';

const EMPTY = {
  code: '',
  target_type: 'product',  // 'product' | 'plan'
  target_id: null,         // se target_type=plan, preenche
  discount_type: 'percentage', // 'percentage' | 'fixed'
  // discount_value mantido como STRING durante edição (pra permitir apagar
  // tudo sem virar 0 fantasma). Convertido para número no submit.
  discount_value: '',
  valid_from: '',
  valid_until: '',
  max_uses: '',
  is_active: true,
};

const formatDateInput = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toISOString().slice(0, 10);
  } catch { return ''; }
};

const CouponDrawer = ({ isOpen, onClose, productId, plans = [], coupon, onSaved }) => {
  const isEdit = !!coupon?.id;
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [topError, setTopError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setTopError('');
    if (coupon) {
      const sourceValue = coupon.discount_type === 'percentage'
        ? coupon.discount_percent
        : coupon.discount_cents;
      setForm({
        code: coupon.code || '',
        target_type: coupon.target_type || 'product',
        target_id: coupon.target_id || null,
        discount_type: coupon.discount_type || 'percentage',
        discount_value: sourceValue != null ? String(sourceValue) : '',
        valid_from: formatDateInput(coupon.valid_from),
        valid_until: formatDateInput(coupon.valid_until),
        max_uses: coupon.max_uses ? String(coupon.max_uses) : '',
        is_active: coupon.is_active !== false,
      });
    } else {
      setForm({ ...EMPTY, target_id: productId });
    }
  }, [isOpen, coupon, productId]);

  const update = (f, v) => {
    setForm((p) => ({ ...p, [f]: v }));
    setErrors((p) => ({ ...p, [f]: undefined }));
  };

  const validate = () => {
    const err = {};
    if (!form.code.trim()) err.code = 'Código é obrigatório';
    if (form.code.length > 32) err.code = 'Máximo 32 caracteres';
    const n = parseFloat(form.discount_value);
    if (!n || n <= 0) err.discount_value = 'Valor deve ser maior que zero';
    else if (form.discount_type === 'percentage' && n > 100) err.discount_value = 'Máximo 100%';
    if (form.target_type === 'plan' && !form.target_id) err.target_id = 'Selecione um plano';
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate() || submitting) return;
    try {
      setSubmitting(true);
      setTopError('');
      const payload = {
        code: form.code.trim().toUpperCase(),
        target_type: form.target_type,
        target_id: form.target_type === 'plan'
          ? Number(form.target_id)
          : productId,
        discount_type: form.discount_type,
        valid_from: form.valid_from || null,
        valid_until: form.valid_until || null,
        max_uses: form.max_uses ? parseInt(form.max_uses, 10) : null,
        is_active: !!form.is_active,
      };
      // V2_035: split por discount_type
      if (form.discount_type === 'percentage') {
        payload.discount_percent = parseFloat(form.discount_value) || 0;
      } else {
        payload.discount_cents = parseInt(form.discount_value, 10) || 0;
      }
      let res;
      if (isEdit) {
        res = await couponsV2API.update(coupon.id, payload);
      } else {
        res = await couponsV2API.create({ ...payload, product_id: productId });
      }
      const saved = res.data?.coupon || res.data?.data || res.data;
      onSaved?.(saved);
      onClose?.();
    } catch (err) {
      const { fieldErrors, message } = parseApiError(err, 'Erro ao salvar cupom');
      if (Object.keys(fieldErrors).length) {
        setErrors((p) => ({ ...p, ...fieldErrors }));
        scrollToFirstError(fieldErrors);
      }
      setTopError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Editar cupom' : 'Novo cupom de desconto'}
      subtitle={isEdit ? form.code : 'Crie um código promocional'}
      icon="mdi:ticket-percent"
      onSubmit={handleSubmit}
      submitting={submitting}
      submitLabel={isEdit ? 'Salvar cupom' : 'Criar cupom'}
    >
      {topError && (
        <div className="drawer-alert drawer-alert--error">
          <Icon icon="mdi:alert-circle" width="16" />
          <div>{topError}</div>
        </div>
      )}

      <div className="drawer-field">
        <span className="drawer-label drawer-label--required">Código</span>
        <input
          className="drawer-input"
          type="text"
          value={form.code}
          onChange={(e) => update('code', e.target.value.replace(/\s/g, '').toUpperCase())}
          placeholder="BLACKFRIDAY10"
          maxLength={32}
          style={{ textTransform: 'uppercase', fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}
        />
        <span className="drawer-help">Pelo menos 3 caracteres. Sem espaços.</span>
        {errors.code && (
          <span className="drawer-error">
            <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.code}
          </span>
        )}
      </div>

      <div className="drawer-field">
        <span className="drawer-label">Aplicar em</span>
        <div className="drawer-toggle-group">
          <button
            type="button"
            className={`drawer-toggle-btn ${form.target_type === 'product' ? 'is-active' : ''}`}
            onClick={() => update('target_type', 'product')}
          >
            <Icon icon="mdi:package-variant" width="18" />
            <span className="drawer-toggle-btn--label">Produto inteiro</span>
            <span className="drawer-toggle-btn--desc">Vale em qualquer plano</span>
          </button>
          <button
            type="button"
            className={`drawer-toggle-btn ${form.target_type === 'plan' ? 'is-active' : ''}`}
            onClick={() => update('target_type', 'plan')}
          >
            <Icon icon="mdi:credit-card-multiple-outline" width="18" />
            <span className="drawer-toggle-btn--label">Plano específico</span>
            <span className="drawer-toggle-btn--desc">Apenas no plano selecionado</span>
          </button>
        </div>
      </div>

      {form.target_type === 'plan' && (
        <div className="drawer-field">
          <span className="drawer-label drawer-label--required">Plano</span>
          <Dropdown
            compact
            placeholder="Selecione um plano"
            value={form.target_id || ''}
            onChange={(v) => update('target_id', Number(v))}
            options={plans.map((p) => ({
              value: p.id,
              label: p.name,
              icon: 'mdi:credit-card-multiple-outline',
            }))}
          />
          {errors.target_id && (
            <span className="drawer-error">
              <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.target_id}
            </span>
          )}
        </div>
      )}

      <div className="drawer-field--row">
        <div className="drawer-field">
          <span className="drawer-label">Tipo de desconto</span>
          <Dropdown
            compact
            value={form.discount_type}
            onChange={(v) => update('discount_type', v)}
            options={[
              { value: 'percentage', label: 'Porcentagem', icon: 'mdi:percent', secondary: '% off' },
              { value: 'fixed', label: 'Valor fixo', icon: 'mdi:currency-brl', secondary: 'R$ off' },
            ]}
          />
        </div>
        <div className="drawer-field">
          <span className="drawer-label drawer-label--required">Valor</span>
          {form.discount_type === 'percentage' ? (
            <div className="drawer-input--with-suffix">
              <input
                className="drawer-input"
                type="text"
                inputMode="numeric"
                value={form.discount_value}
                onChange={(e) => {
                  // só dígitos + ponto (permite 10.5%)
                  const cleaned = e.target.value.replace(/[^\d.]/g, '');
                  update('discount_value', cleaned);
                }}
                placeholder="0"
              />
              <span className="drawer-input-suffix">%</span>
            </div>
          ) : (
            <div className="drawer-input--with-suffix" style={{ position: 'relative' }}>
              <input
                className="drawer-input"
                type="text"
                inputMode="numeric"
                value={centsToDisplay(parseFloat(form.discount_value) || 0)}
                onChange={(e) => update('discount_value', String(parseInputToCents(e.target.value)))}
                placeholder="0,00"
                style={{ paddingLeft: 36 }}
              />
              <span style={{ position: 'absolute', left: 14, top: '50%', transform: 'translateY(-50%)', color: '#6b7280', fontSize: 13, fontWeight: 600 }}>R$</span>
            </div>
          )}
          {errors.discount_value && (
            <span className="drawer-error">
              <Icon icon="mdi:alert-circle-outline" width="12" /> {errors.discount_value}
            </span>
          )}
        </div>
      </div>

      <div className="drawer-field--row">
        <div className="drawer-field">
          <span className="drawer-label">Válido a partir de</span>
          <input
            className="drawer-input"
            type="date"
            value={form.valid_from}
            onChange={(e) => update('valid_from', e.target.value)}
          />
        </div>
        <div className="drawer-field">
          <span className="drawer-label">Válido até</span>
          <input
            className="drawer-input"
            type="date"
            value={form.valid_until}
            onChange={(e) => update('valid_until', e.target.value)}
          />
        </div>
      </div>

      <div className="drawer-field">
        <span className="drawer-label">Limite de usos (opcional)</span>
        <input
          className="drawer-input"
          type="number"
          min={1}
          value={form.max_uses || ''}
          onChange={(e) => update('max_uses', e.target.value ? parseInt(e.target.value, 10) : null)}
          placeholder="Deixe vazio pra ilimitado"
        />
      </div>

      <div className="drawer-switch">
        <div className="drawer-switch__label">
          <span className="drawer-switch__label-title">Cupom ativo</span>
          <span className="drawer-switch__label-desc">Quando desativado, não pode ser aplicado.</span>
        </div>
        <button
          type="button"
          className={`drawer-switch__input ${form.is_active ? 'is-on' : ''}`}
          onClick={() => update('is_active', !form.is_active)}
          aria-pressed={form.is_active}
        />
      </div>
    </Drawer>
  );
};

export default CouponDrawer;
